// @ts-nocheck
import { Box } from '@mui/material';
import { WarningCircle, XCircle } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import * as PropTypes from 'prop-types';
import './BookingCapacityBanner.css';

/**
 * Functional React component for displaying trip capacity warnings
 *
 * @namespace Components
 *
 * @param {Object} props - The component's properties
 * @param {number|null} [props.remainingCapacity] - Remaining capacity (null means unlimited)
 * @param {boolean} [props.isExceeded] - Whether capacity has been exceeded
 * @param {number} [props.totalSelected] - Total passengers selected
 * @param {number} [props.tripCapacity] - Total trip capacity
 *
 * @returns {JSX.Element|null} React element representing the capacity banner or null if not needed
 */
const BookingCapacityBanner = ({
  remainingCapacity,
  isExceeded,
  totalSelected
}) => {
  const { t } = useTranslation(['trip']);

  // Don't show banner if capacity is unlimited or greater than 5
  if (
    remainingCapacity === null ||
    remainingCapacity === undefined ||
    (!isExceeded && remainingCapacity > 5)
  ) {
    return null;
  }

  // Determine banner type and content
  let bannerType = 'warning'; // 'warning', 'error', 'exceeded'
  let icon = <WarningCircle size={20} weight='fill' />;
  let title = '';
  let subtitle = '';

  if (remainingCapacity <= 0) {
    // Full/oversold capacity scenario - no spots left on the trip (or already oversold)
    // This check must come BEFORE isExceeded to handle oversold trips correctly
    bannerType = 'error';
    icon = <XCircle size={20} weight='fill' />;
    title = t('trip:capacityBannerFull');
    subtitle = t('trip:capacityBannerFullSubtitle');
  } else if (isExceeded) {
    // Capacity exceeded scenario - user selections exceed available capacity
    bannerType = 'exceeded';
    icon = <XCircle size={20} weight='fill' />;
    title = t('trip:capacityBannerExceeded', {
      selected: totalSelected,
      available: remainingCapacity
    });
  } else if (remainingCapacity <= 5) {
    // Low capacity warning - 5 or fewer spots remaining
    bannerType = 'warning';
    icon = <WarningCircle size={20} weight='fill' />;
    title = t('trip:capacityBannerLow', { count: remainingCapacity });
    subtitle = t('trip:capacityBannerLowSubtitle');
  }

  return (
    <Box
      className={`booking-capacity-banner booking-capacity-banner--${bannerType}`}
      data-testid='booking-capacity-banner'
    >
      <div className='booking-capacity-banner__icon'>{icon}</div>
      <div className='booking-capacity-banner__content'>
        <div className='booking-capacity-banner__title'>{title}</div>
        {subtitle && (
          <div className='booking-capacity-banner__subtitle'>{subtitle}</div>
        )}
      </div>
    </Box>
  );
};

BookingCapacityBanner.propTypes = {
  remainingCapacity: PropTypes.number,
  isExceeded: PropTypes.bool,
  totalSelected: PropTypes.number,
  tripCapacity: PropTypes.number
};

export default BookingCapacityBanner;
